/**
 * Generates structured data schema for a movie
 * @param {Object} movie - Movie object containing details
 * @returns {Object} Schema.org MovieSchema object
 */
export const generateMovieSchema = (movie) => {
  if (!movie) return null;

  const PLACEHOLDER_IMAGE = "https://moviehub888.xyz/movie.svg";
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Movie",
    "name": movie.title || "Untitled Movie",
    "url": `https://moviehub888.xyz/movie/${encodeURIComponent(movie.slug)}` || '',
    "genre": movie.genres || ["Uncategorized"],
    "dateCreated": movie.createdAt || new Date().toISOString(),
    "image": PLACEHOLDER_IMAGE // Default placeholder
  };

  // Add optional fields if they exist
  if (movie.description) {
    schema.description = movie.description;
  }

  // Override placeholder with actual image if available
  if (movie.images && Array.isArray(movie.images) && movie.images.length > 0 && movie.images[0]?.url) {
    schema.image = movie.images[0].url;
  } else if (movie.poster) {
    schema.image = movie.poster;
  }

  if (movie.releaseDate) {
    schema.datePublished = movie.releaseDate;
  }

  if (movie.cast && Array.isArray(movie.cast) && movie.cast.length > 0) {
    schema.actor = movie.cast.map(actor => ({
      "@type": "Person",
      "name": actor?.name || "Unknown Actor"
    })).filter(actor => actor.name !== "Unknown Actor");
  }

  if (movie.duration) {
    schema.duration = movie.duration;
  }

  if (movie.director) {
    schema.director = {
      "@type": "Person",
      "name": movie.director
    };
  }

  if (movie.language) {
    schema.inLanguage = movie.language;
  }

  if (typeof movie.rating === 'number' && typeof movie.ratingCount === 'number') {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": movie.rating.toFixed(1),
      "bestRating": "10",
      "worstRating": "1",
      "ratingCount": movie.ratingCount
    };
  }

  return schema;
};

/**
 * Generates structured data schema for a list of movies
 * @param {Array} movies - Array of movie objects
 * @returns {Object} Schema.org ItemList object
 */
export const generateMovieListSchema = (movies) => {
  if (!Array.isArray(movies) || movies.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "numberOfItems": movies.length,
    "itemListElement": movies.map((movie, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": generateMovieSchema(movie)
    }))
  };
};

/**
 * Generates breadcrumb schema for navigation
 * @param {Array} items - Array of breadcrumb items
 * @returns {Object} Schema.org BreadcrumbList object
 */
export const generateBreadcrumbSchema = (items) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://moviehub888.xyz${item.path}`
    }))
  };
};
